/**
 * salaryLogic.js
 *
 * Business logic for the "Stipendio Ricevuto" (Salary Received) feature.
 *
 * Workflow:
 * 1. The user selects a subset of credit transactions for a person in a date range.
 * 2. The system computes a coefficient = floor((realAmount / selectedTotal) * 100) / 100.
 * 3. Each selected transaction is rescaled: newAmount = floor(oldAmount * coeff * 100) / 100.
 * 4. Any residual cents (realAmount - sum(newAmounts)) are added to the last selected
 *    transaction (sorted chronologically, i.e. the most recent one is the "last").
 * 5. A closing "debt" movement is created for the person to zero out the credit period.
 */

/**
 * Returns all income transactions linked to a person as "credit" entries,
 * filtered by a date range [startDate, endDate] inclusive.
 *
 * @param {Array} incomes - The full incomes collection
 * @param {string} personName - The employer / person name
 * @param {string} startDate - ISO date string 'YYYY-MM-DD'
 * @param {string} endDate   - ISO date string 'YYYY-MM-DD'
 * @returns {Array} Filtered and chronologically sorted transactions (oldest first)
 */
export function getSalaryPeriodTransactions(incomes, personName, startDate, endDate) {
  if (!Array.isArray(incomes) || !personName || !startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  // Make end inclusive by going to EOD
  end.setHours(23, 59, 59, 999);

  return incomes
    .filter((tx) => {
      if (!tx.isDebtTransaction) return false;
      if (tx.debtType !== 'credit') return false;
      if ((tx.debtPersonName || '').trim().toLowerCase() !== personName.trim().toLowerCase()) return false;

      const txDate = tx.date ? new Date(tx.date) : null;
      if (!txDate || isNaN(txDate.getTime())) return false;

      return txDate >= start && txDate <= end;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date)); // oldest first
}

/**
 * Computes the salary coefficient, truncated (floor) to 2 decimal places.
 *
 * @param {number} realAmount     - The actual salary received
 * @param {number} selectedTotal  - The sum of the selected transactions
 * @returns {number} coefficient ≥ 0
 */
export function computeSalaryCoefficient(realAmount, selectedTotal) {
  if (!selectedTotal || selectedTotal <= 0) return 0;
  const raw = realAmount / selectedTotal;
  return Math.floor(raw * 100) / 100;
}

/**
 * Computes the new amounts for each selected transaction.
 * The residual (difference between realAmount and the sum of rescaled amounts)
 * is added to the LAST transaction chronologically (last element in the sorted array).
 *
 * @param {Array}  transactions  - Sorted (oldest first) selected transactions
 * @param {number} realAmount    - The actual salary received
 * @param {number} coefficient   - The truncated coefficient
 * @returns {Array<{id: string, newAmount: number}>}
 */
export function computeRescaledAmounts(transactions, realAmount, coefficient) {
  if (!transactions || transactions.length === 0) return [];

  const rescaled = transactions.map((tx) => ({
    id: tx.id,
    newAmount: Math.floor(tx.amount * coefficient * 100) / 100,
  }));

  const sumRescaled = rescaled.reduce((sum, item) => sum + item.newAmount, 0);
  // Round to avoid floating-point noise
  const residual = Math.round((realAmount - sumRescaled) * 100) / 100;

  if (residual !== 0 && rescaled.length > 0) {
    // Add residual to the last transaction (most recent, since array is sorted oldest→newest)
    rescaled[rescaled.length - 1].newAmount =
      Math.round((rescaled[rescaled.length - 1].newAmount + residual) * 100) / 100;
  }

  return rescaled;
}

/**
 * Builds the closing debt object for the salary period.
 *
 * A "debt" here means: the employer has now paid, so we owe them nothing anymore.
 * This is a movement of type 'debt' (we received the money), which will be stored
 * in the debts collection and linked to a real wallet.
 *
 * @param {string} personName  - The employer / person name
 * @param {number} realAmount  - The actual salary received
 * @param {string} walletId    - The wallet that received the salary
 * @param {string} date        - ISO date string 'YYYY-MM-DD'
 * @param {string} time        - Time string 'HH:MM'
 * @returns {Object} Debt object ready for addDebtTransaction / Firestore
 */
export function buildClosingDebt(personName, realAmount, walletId, date, time) {
  return {
    personName,
    amount: realAmount,
    type: 'debt', // the person paid us → we owe them → reduces our credit
    walletId,
    description: `Stipendio ricevuto – chiusura periodo`,
    date,
    time: time || '00:00',
  };
}

/**
 * Full salary processing pipeline.
 * Returns all the data needed to perform the Firestore updates atomically in the caller.
 *
 * @param {object} params
 * @param {Array}   params.incomes      - Full incomes collection
 * @param {string}  params.personName   - Employer name
 * @param {number}  params.realAmount   - Actual salary received
 * @param {string}  params.walletId     - Destination wallet ID
 * @param {string}  params.startDate    - Range start 'YYYY-MM-DD'
 * @param {string}  params.endDate      - Range end 'YYYY-MM-DD'
 * @param {Set}     params.selectedIds  - Set of transaction IDs to include
 * @returns {{ rescaledAmounts: Array, closingDebt: Object, coefficient: number, selectedTotal: number }}
 */
export function processSalary({ incomes, personName, realAmount, walletId, startDate, endDate, selectedIds }) {
  const allPeriodTx = getSalaryPeriodTransactions(incomes, personName, startDate, endDate);
  const selectedTx = allPeriodTx.filter((tx) => selectedIds.has(tx.id));

  const selectedTotal = selectedTx.reduce((sum, tx) => sum + tx.amount, 0);
  const coefficient = computeSalaryCoefficient(realAmount, selectedTotal);
  const rescaledAmounts = computeRescaledAmounts(selectedTx, realAmount, coefficient);

  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const closingDebt = buildClosingDebt(personName, realAmount, walletId, endDate, time);

  return { rescaledAmounts, closingDebt, coefficient, selectedTotal };
}
